import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  FilterToolbarSearch,
  FormLabel,
  filterToolbarClinicClassName,
  filterToolbarControlLgClassName,
  filterToolbarControlSmClassName,
  filterToolbarSearchGrowClassName,
  Input,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { getVisitTypeDisplayName } from '../hooks/useVisitTypeOptions';
import { useOpenOasisReportParams } from '../hooks/reportParams/useOpenOasisReportParams';
import { PermissionSlug } from '../constants/featureFlags';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { setStoredIncludeImportedPatients } from '../utils/populationPreferences';
import {
  exportOpenOasisReportReportsOpenOasisExportGet,
  getOpenOasisReportOptionsReportsOpenOasisOptionsGet,
  getOpenOasisReportReportsOpenOasisGet,
} from '../services/api/sdk.gen';
import type {
  OpenOasisReportOptions,
  OpenOasisReportRow,
  OpenOasisSortBy,
  OpenOasisSortDirection,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';
import {
  coerceReportPageSize,
  displayDash,
  readDelimitedParam,
} from '../utils/reportDisplay';
import { ReportLink } from '../components/reporting/ReportLink';
import { ReportMultiSelectFilter } from '../components/reporting/ReportMultiSelectFilter';
import { ExcludePreTakeoverFilterCheckbox } from '../components/filters/ExcludePreTakeoverFilterCheckbox';
import { SupportMessage } from '../components/support/ContactSupportLink';
import { createPortal } from 'react-dom';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import { useBreadcrumbs } from '../components/breadcrumbs/BreadcrumbContext';

const ALL_CLINICS_VALUE = '__all_clinics__';
const ALL_STATUSES_VALUE = '__all_statuses__';
const ALL_VISIT_TYPES_VALUE = '__all_visit_types__';
const UNASSIGNED_CLINICIAN_VALUE = '__unassigned__';
const ALL_PAYOR_TYPES_VALUE = '__all_payor_types__';

const buildOpenOasisPopulationQuery = ({
  includeTestRows,
  includeInactiveEpisodes,
  hideImportedPatients,
  excludePreTakeover = false,
}: {
  includeTestRows: boolean;
  includeInactiveEpisodes: boolean;
  hideImportedPatients: boolean;
  excludePreTakeover?: boolean;
}) => ({
  include_test_rows: includeTestRows,
  include_inactive_episodes: includeInactiveEpisodes,
  hide_imported_patients: hideImportedPatients,
  exclude_pre_takeover: excludePreTakeover,
});

const validDaysValue = (value: number | null): number | null =>
  value !== null && Number.isInteger(value) && value >= 0 ? value : null;

const parseDaysInput = (value: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) return null;
  return parsed;
};

const formatDaysInput = (value: number | null): string =>
  value === null ? '' : String(value);

const patientName = (row: OpenOasisReportRow) =>
  `${row.patient_last_name}, ${row.patient_first_name}`;

const openOasisColumns: ReportTableColumn<OpenOasisReportRow>[] = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: (row: OpenOasisReportRow) => displayDash(row.clinic_name),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'patient',
    header: 'Patient',
    accessor: patientName,
    render: (_value: unknown, row: OpenOasisReportRow) => (
      <ReportLink to={`/patients/${row.patient_id}`}>
        {patientName(row)}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: OpenOasisReportRow) => displayDash(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'visit_type',
    header: 'OASIS',
    accessor: (row: OpenOasisReportRow) =>
      row.alias_name ?? getVisitTypeDisplayName(row.visit_type),
    render: (_value: unknown, row: OpenOasisReportRow) => (
      <ReportLink to={`/patients/${row.patient_id}/visits/${row.visit_id}`}>
        {row.alias_name ?? getVisitTypeDisplayName(row.visit_type)}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'target_date',
    header: 'Target Date',
    accessor: (row: OpenOasisReportRow) =>
      formatDateToMMDDYYYY(row.target_date),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'days_outstanding',
    header: 'Days Outstanding',
    accessor: (row: OpenOasisReportRow) => row.days_outstanding,
    align: 'right' as const,
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'status',
    header: 'Status',
    accessor: (row: OpenOasisReportRow) => row.status.replace(/_/g, ' '),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'assigned_clinician',
    header: 'Currently Assigned',
    accessor: (row: OpenOasisReportRow) =>
      displayDash(row.assigned_clinician_name),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'episode_payor',
    header: 'Episode Payor',
    accessor: (row: OpenOasisReportRow) => displayDash(row.episode_payor_name),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'payor_type',
    header: 'Payor Type',
    accessor: (row: OpenOasisReportRow) =>
      row.episode_payor_type
        ? row.episode_payor_type.replace(/_/g, ' ')
        : 'Unclassified',
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'notes',
    header: 'Notes',
    accessor: (row: OpenOasisReportRow) => displayDash(row.notes),
    // Notes are clamped to two lines, so anything longer used to be readable
    // only by opening the visit. Hovering shows the whole note in place.
    render: (value, row) => {
      if (!row.notes) {
        return <span className='line-clamp-2 max-w-sm'>{String(value)}</span>;
      }
      // tabIndex so the full note is reachable by keyboard, not hover only.
      const cell = (
        <span tabIndex={0} className='line-clamp-2 max-w-sm cursor-help'>
          {String(value)}
        </span>
      );
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>{cell}</TooltipTrigger>
            <TooltipContent
              side='bottom'
              className='max-w-md whitespace-pre-wrap text-xs'
            >
              {row.notes}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
    enableSorting: true,
    minWidth: 240,
  },
];

export function OpenOasisReportWithQueryState({
  showBackLink = true,
  toolbarTarget = null,
  excludePreTakeover = false,
  onExcludePreTakeoverChange,
}: {
  showBackLink?: boolean;
  toolbarTarget?: HTMLElement | null;
  /** EMR-3174: OASIS Manager Incomplete passes the shared pre-takeover toggle. */
  excludePreTakeover?: boolean;
  /** When set, render the exclude checkbox at the end of the filter row. */
  onExcludePreTakeoverChange?: (checked: boolean) => void;
}) {
  return (
    <OpenOasisReport
      showBackLink={showBackLink}
      toolbarTarget={toolbarTarget}
      excludePreTakeover={excludePreTakeover}
      onExcludePreTakeoverChange={onExcludePreTakeoverChange}
    />
  );
}

export default function OpenOasisReportPage() {
  // Not in reportCatalog; drop this if it joins, or the name shows twice.
  useBreadcrumbs([{ label: 'Open OASIS' }]);
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) {
    return <ReportsPermissionAlert />;
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 overflow-auto'>
        <OpenOasisReportWithQueryState />
      </div>
    </div>
  );
}

export function OpenOasisReport({
  showBackLink = true,
  toolbarTarget = null,
  excludePreTakeover = false,
  onExcludePreTakeoverChange,
}: {
  showBackLink?: boolean;
  /** Where to put Export and the population settings instead of their own row. */
  toolbarTarget?: HTMLElement | null;
  excludePreTakeover?: boolean;
  /** When set, render the exclude checkbox at the end of the filter row. */
  onExcludePreTakeoverChange?: (checked: boolean) => void;
}) {
  const {
    params,
    searchInput,
    setSearchInput,
    setFilters,
    setSorting,
    isSearchPending,
    setPagination,
    tableQuery,
    exportQuery,
  } = useOpenOasisReportParams();
  const [options, setOptions] = useState<OpenOasisReportOptions | null>(null);
  const [rows, setRows] = useState<OpenOasisReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // A failure is worth offering support for; a filter the user can fix in one
  // click is not. Both render in the same Alert, so the kind travels with it.
  const [error, setError] = useState<{
    message: string;
    supportable: boolean;
  } | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const location = useLocation();
  const reportsBackHref = useReportsBackHref();
  const page = params.page > 0 ? params.page : 1;
  const pageSize = coerceReportPageSize(params.size);
  const sortBy = params.sort as OpenOasisSortBy;
  const sortDirection = params.dir as OpenOasisSortDirection;
  const paramsDaysMin = validDaysValue(params.days_min);
  const paramsDaysMax = validDaysValue(params.days_max);
  const [daysMinText, setDaysMinText] = useState(() =>
    formatDaysInput(paramsDaysMin)
  );
  const [daysMaxText, setDaysMaxText] = useState(() =>
    formatDaysInput(paramsDaysMax)
  );
  const [debouncedDaysMin, setDebouncedDaysMin] = useState(paramsDaysMin);
  const [debouncedDaysMax, setDebouncedDaysMax] = useState(paramsDaysMax);
  const [hasEditedDays, setHasEditedDays] = useState(false);
  const pendingDaysMin = parseDaysInput(daysMinText);
  const pendingDaysMax = parseDaysInput(daysMaxText);
  const isDaysDebouncing =
    pendingDaysMin !== debouncedDaysMin || pendingDaysMax !== debouncedDaysMax;
  const hasInvalidDateRange = Boolean(params.from) !== Boolean(params.to);
  const {
    page: _tablePage,
    page_size: _tablePageSize,
    ...tableQueryWithoutAliases
  } = tableQuery;
  void _tablePage;
  void _tablePageSize;
  const reportTableQuery = {
    ...tableQueryWithoutAliases,
    statuses: params.status || undefined,
    visit_types: params.visit_type || undefined,
    unassigned: params.unassigned || false,
    hide_imported_patients: params.hideImportedPatients,
    exclude_pre_takeover: excludePreTakeover,
    days_outstanding_min: debouncedDaysMin ?? undefined,
    days_outstanding_max: debouncedDaysMax ?? undefined,
  };
  const {
    page: _exportPage,
    page_size: _exportPageSize,
    ...exportQueryWithoutAliases
  } = exportQuery;
  void _exportPage;
  void _exportPageSize;
  const reportExportQuery = {
    ...exportQueryWithoutAliases,
    statuses: params.status || undefined,
    visit_types: params.visit_type || undefined,
    unassigned: params.unassigned || false,
    hide_imported_patients: params.hideImportedPatients,
    exclude_pre_takeover: excludePreTakeover,
    days_outstanding_min: debouncedDaysMin ?? undefined,
    days_outstanding_max: debouncedDaysMax ?? undefined,
  };

  const updateReportParams = useCallback(
    (updates: Partial<typeof params>, _resetPage = true) => {
      if ('q' in updates) {
        setSearchInput(updates.q ?? '');
        return;
      }
      setFilters(updates, _resetPage);
    },
    [setFilters, setSearchInput]
  );

  useEffect(() => {
    setDaysMinText(formatDaysInput(paramsDaysMin));
    setDaysMaxText(formatDaysInput(paramsDaysMax));
    setDebouncedDaysMin(paramsDaysMin);
    setDebouncedDaysMax(paramsDaysMax);
  }, [paramsDaysMax, paramsDaysMin]);

  useEffect(() => {
    const hasInvalidUrlDays =
      (location.search.includes('days_min') &&
        typeof params.days_min === 'number' &&
        !Number.isNaN(params.days_min) &&
        params.days_min !== paramsDaysMin) ||
      (location.search.includes('days_max') &&
        typeof params.days_max === 'number' &&
        !Number.isNaN(params.days_max) &&
        params.days_max !== paramsDaysMax);
    if (!hasInvalidUrlDays && !isDaysDebouncing) return;

    const timeoutId = window.setTimeout(() => {
      setDebouncedDaysMin(pendingDaysMin);
      setDebouncedDaysMax(pendingDaysMax);
      updateReportParams(
        {
          days_min: pendingDaysMin,
          days_max: pendingDaysMax,
        },
        hasEditedDays
      );
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [
    hasEditedDays,
    isDaysDebouncing,
    location.search,
    params.days_max,
    params.days_min,
    paramsDaysMax,
    paramsDaysMin,
    pendingDaysMax,
    pendingDaysMin,
    updateReportParams,
  ]);

  const updateDaysMin = (value: string) => {
    setHasEditedDays(true);
    const nextMin = parseDaysInput(value);
    const currentMax = parseDaysInput(daysMaxText);
    setDaysMinText(formatDaysInput(nextMin));
    if (nextMin !== null && currentMax !== null && nextMin > currentMax) {
      setDaysMaxText(String(nextMin));
    }
  };

  const updateDaysMax = (value: string) => {
    setHasEditedDays(true);
    const nextMax = parseDaysInput(value);
    const currentMin = parseDaysInput(daysMinText);
    setDaysMaxText(formatDaysInput(nextMax));
    if (nextMax !== null && currentMin !== null && nextMax < currentMin) {
      setDaysMinText(String(nextMax));
    }
  };

  useEffect(() => {
    let cancelled = false;
    getOpenOasisReportOptionsReportsOpenOasisOptionsGet({
      query: {
        ...buildOpenOasisPopulationQuery({
          includeTestRows: params.include_test_rows,
          includeInactiveEpisodes: params.include_inactive_episodes,
          hideImportedPatients: params.hideImportedPatients,
          excludePreTakeover,
        }),
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError({
            message: 'Unable to load Open OASIS report. Please try again.',
            supportable: true,
          });
          return;
        }
        setOptions(response.data);
      })
      .catch(error => {
        console.error('Failed to load Open OASIS options', error);
        if (!cancelled)
          setError({
            message: 'Unable to load Open OASIS report. Please try again.',
            supportable: true,
          });
      });
    return () => {
      cancelled = true;
    };
  }, [
    excludePreTakeover,
    params.hideImportedPatients,
    params.include_inactive_episodes,
    params.include_test_rows,
  ]);

  useEffect(() => {
    if (hasInvalidDateRange) {
      setIsLoading(false);
      setRows([]);
      setTotal(0);
      setError({
        message: 'Select both From and To target dates, or clear both.',
        supportable: false,
      });
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getOpenOasisReportReportsOpenOasisGet({
      query: {
        ...reportTableQuery,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setError({
            message:
              getBusinessErrorMessage({
                response: {
                  status: response.response?.status,
                  data: response.error,
                },
              }) ?? 'Unable to load Open OASIS report. Please try again.',
            supportable: true,
          });
          setRows([]);
          setTotal(0);
          return;
        }
        setRows(response.data.items);
        setTotal(response.data.total);
      })
      .catch(error => {
        console.error('Failed to load Open OASIS report', error);
        if (!cancelled) {
          setError({
            message:
              getBusinessErrorMessage(error) ??
              'Unable to load Open OASIS report. Please try again.',
            supportable: true,
          });
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    debouncedDaysMax,
    debouncedDaysMin,
    hasInvalidDateRange,
    page,
    pageSize,
    params.clinic,
    params.clinician,
    params.days_max,
    params.days_min,
    params.from,
    params.include_inactive_episodes,
    params.include_test_rows,
    params.hideImportedPatients,
    params.payor_type,
    params.q,
    params.status,
    params.to,
    params.unassigned,
    params.visit_type,
    excludePreTakeover,
    sortBy,
    sortDirection,
  ]);

  const sorting: ReportTableSort = { id: sortBy, direction: sortDirection };
  const pagination: ReportTablePaginationState = { page, pageSize };

  const download = async () => {
    if (isSearchPending) return;
    if (hasInvalidDateRange) {
      setError({
        message: 'Select both From and To target dates, or clear both.',
        supportable: false,
      });
      return;
    }
    setIsExporting(true);
    try {
      const response = await exportOpenOasisReportReportsOpenOasisExportGet({
        query: reportExportQuery,
        responseType: 'blob' as const,
      });
      if (response.error || !response.data) {
        setError({
          message: await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download Open OASIS report. Please try again.'
          ),
          supportable: true,
        });
        return;
      }
      downloadResponseAsFile(response as never, 'open-oasis-report.xlsx');
    } catch (error) {
      console.error('Failed to download Open OASIS report', error);
      setError({
        message: await getUserFacingApiMessageAsync(
          error,
          'Unable to download Open OASIS report. Please try again.'
        ),
        supportable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const emptyState = error ? (
    <EmptyState
      role='alert'
      title={error.message}
      description='Please try again.'
    />
  ) : (
    <EmptyState
      title='No incomplete OASIS visits match these filters'
      description='Adjust filters and try again.'
    />
  );

  const toolbar = (
    <div className='flex flex-wrap items-center gap-2'>
      <Button
        variant='outline'
        size='sm'
        onClick={() => void download()}
        disabled={
          isLoading ||
          isExporting ||
          isSearchPending ||
          hasInvalidDateRange ||
          isDaysDebouncing
        }
      >
        {isExporting ? (
          <Loader2 className='h-4 w-4 animate-spin' />
        ) : (
          <Download className='h-4 w-4' />
        )}
        Export
      </Button>
      <ReportPopulationControls
        showImportedPatients={true}
        includeTestRows={params.include_test_rows}
        includeInactiveEpisodes={params.include_inactive_episodes}
        includeImportedPatients={!params.hideImportedPatients}
        onIncludeTestRowsChange={checked =>
          updateReportParams({ include_test_rows: checked })
        }
        onIncludeInactiveEpisodesChange={checked =>
          updateReportParams({ include_inactive_episodes: checked })
        }
        onIncludeImportedPatientsChange={checked => {
          setStoredIncludeImportedPatients(checked);
          updateReportParams({ hideImportedPatients: !checked });
        }}
      />
    </div>
  );

  return (
    <div className='space-y-4'>
      {showBackLink && (
        <Link
          to={reportsBackHref}
          className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
        >
          <ArrowLeft className='h-4 w-4' /> Reports
        </Link>
      )}
      {toolbarTarget ? (
        createPortal(toolbar, toolbarTarget)
      ) : (
        <div className='flex items-start gap-4'>{toolbar}</div>
      )}
      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>
            {error.supportable ? (
              <SupportMessage
                message={error.message}
                subject='Open OASIS report error'
                hiddenContext={{ error_detail: error.message }}
              />
            ) : (
              error.message
            )}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className='space-y-3 rounded-lg border bg-card p-4'>
        {/* One wrap row so Payor is not stranded alone above a hard-coded
            second date row. Search grows beside siblings (`sm:w-auto` overrides
            the shared grow recipe's `w-full`, which otherwise owns the first
            line by itself in a flex-wrap toolbar). */}
        <div className='flex flex-wrap items-end gap-3'>
          <FilterToolbarSearch
            value={searchInput}
            onChange={value => updateReportParams({ q: value || '' })}
            label='Search'
            placeholder='Name or MRN...'
            containerClassName={`${filterToolbarSearchGrowClassName} basis-full sm:w-auto sm:basis-64`}
            className='w-full'
          />
          <div className={filterToolbarClinicClassName}>
            <FormLabel htmlFor='open-oasis-clinic-filter' showTooltip={false}>
              Clinic
            </FormLabel>
            <Select
              value={params.clinic || ALL_CLINICS_VALUE}
              onValueChange={value =>
                updateReportParams({
                  clinic: value === ALL_CLINICS_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger
                id='open-oasis-clinic-filter'
                selectSize='default'
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS_VALUE}>All clinics</SelectItem>
                {options?.clinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='open-oasis-status-filter' showTooltip={false}>
              Status
            </FormLabel>
            <Select
              value={params.status || ALL_STATUSES_VALUE}
              onValueChange={value =>
                updateReportParams({
                  status: value === ALL_STATUSES_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger
                id='open-oasis-status-filter'
                selectSize='default'
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES_VALUE}>All statuses</SelectItem>
                {options?.statuses.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={filterToolbarControlLgClassName}>
            <FormLabel
              htmlFor='open-oasis-visit-type-filter'
              showTooltip={false}
            >
              Visit Type
            </FormLabel>
            <Select
              value={params.visit_type || ALL_VISIT_TYPES_VALUE}
              onValueChange={value =>
                updateReportParams({
                  visit_type: value === ALL_VISIT_TYPES_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger
                id='open-oasis-visit-type-filter'
                selectSize='default'
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VISIT_TYPES_VALUE}>
                  All visit types
                </SelectItem>
                {options?.visit_types.map(visitType => (
                  <SelectItem key={visitType.value} value={visitType.value}>
                    {visitType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={filterToolbarControlLgClassName}>
            <ReportMultiSelectFilter
              label='Clinician'
              value={
                params.unassigned
                  ? UNASSIGNED_CLINICIAN_VALUE
                  : params.clinician
              }
              options={
                options?.assigned_clinicians.map(clinician => ({
                  id: clinician.id,
                  label: clinician.name,
                })) ?? []
              }
              allLabel='All clinicians'
              leadingOptions={[
                { id: UNASSIGNED_CLINICIAN_VALUE, label: 'Unassigned' },
              ]}
              deferApply
              exclusiveOptionId={UNASSIGNED_CLINICIAN_VALUE}
              onChange={nextValue => {
                const next = readDelimitedParam(nextValue);
                const hasUnassigned = next.includes(UNASSIGNED_CLINICIAN_VALUE);
                updateReportParams({
                  clinician: hasUnassigned
                    ? ''
                    : next
                        .filter(value => value !== UNASSIGNED_CLINICIAN_VALUE)
                        .join(','),
                  unassigned: hasUnassigned,
                });
              }}
            />
          </div>
          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='open-oasis-payor-filter' showTooltip={false}>
              Payor Type
            </FormLabel>
            <Select
              value={params.payor_type || ALL_PAYOR_TYPES_VALUE}
              onValueChange={value =>
                updateReportParams({
                  payor_type: value === ALL_PAYOR_TYPES_VALUE ? '' : value,
                })
              }
            >
              <SelectTrigger
                id='open-oasis-payor-filter'
                selectSize='default'
                className='w-full'
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_PAYOR_TYPES_VALUE}>
                  All payor types
                </SelectItem>
                {options?.payor_types.map(payorType => (
                  <SelectItem key={payorType.value} value={payorType.value}>
                    {payorType.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='open-oasis-from' showTooltip={false}>
              From
            </FormLabel>
            <DatePicker
              id='open-oasis-from'
              className='w-full'
              typable
              showClearButton
              clearButtonLabel='Clear from date'
              date={params.from ?? undefined}
              onDateChange={d => updateReportParams({ from: d ?? null })}
            />
          </div>
          <div className={filterToolbarControlSmClassName}>
            <FormLabel htmlFor='open-oasis-to' showTooltip={false}>
              To
            </FormLabel>
            <DatePicker
              id='open-oasis-to'
              className='w-full'
              typable
              showClearButton
              clearButtonLabel='Clear to date'
              date={params.to ?? undefined}
              onDateChange={d => updateReportParams({ to: d ?? null })}
            />
          </div>
          <div className='w-28 shrink-0'>
            <FormLabel htmlFor='open-oasis-days-min' showTooltip={false}>
              Days Min
            </FormLabel>
            <Input
              id='open-oasis-days-min'
              type='number'
              min={0}
              step={1}
              inputMode='numeric'
              value={daysMinText}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateDaysMin(event.target.value)
              }
            />
          </div>
          <div className='w-28 shrink-0'>
            <FormLabel htmlFor='open-oasis-days-max' showTooltip={false}>
              Days Max
            </FormLabel>
            <Input
              id='open-oasis-days-max'
              type='number'
              min={0}
              step={1}
              inputMode='numeric'
              value={daysMaxText}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                updateDaysMax(event.target.value)
              }
            />
          </div>
          {onExcludePreTakeoverChange ? (
            <ExcludePreTakeoverFilterCheckbox
              id='oasis-incomplete-exclude-pre-takeover'
              className='flex h-8 items-center gap-2 self-end'
              checked={excludePreTakeover}
              onCheckedChange={onExcludePreTakeoverChange}
            />
          ) : null}
        </div>
      </div>

      <ReportTable
        data={rows}
        columns={openOasisColumns}
        getRowId={row => row.visit_id}
        caption='Incomplete OASIS visits'
        sorting={sorting}
        onSortingChange={next => {
          setSorting(
            (next?.id as OpenOasisSortBy) ?? 'days_outstanding',
            next?.direction ?? 'desc'
          );
        }}
        isLoading={isLoading}
        emptyState={emptyState}
        pagination={{
          ...pagination,
          total,
          totalPages: Math.ceil(total / pageSize),
        }}
        onPaginationChange={next => {
          setPagination(next.page, next.pageSize);
        }}
      />
    </div>
  );
}
